import { useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  BookOpen, Rocket, ClipboardList, Gauge, Eye, ShieldCheck, Building2, Users, HelpCircle,
  ChevronRight, ChevronLeft,
} from 'lucide-react'
import { cn } from '@/lib/utils'

import introduzione from '@/docs/introduzione.md?raw'
import primiPassi from '@/docs/primi-passi.md?raw'
import valutazione from '@/docs/valutazione.md?raw'
import risultati from '@/docs/risultati.md?raw'
import validazione from '@/docs/validazione.md?raw'
import registro from '@/docs/registro.md?raw'
import istituto from '@/docs/istituto.md?raw'
import ruoli from '@/docs/ruoli.md?raw'
import faq from '@/docs/faq.md?raw'

const CHAPTERS = [
  { slug: 'introduzione', title: 'Che cos\'è ValutazIA', icon: BookOpen, content: introduzione },
  { slug: 'primi-passi', title: 'Primi passi', icon: Rocket, content: primiPassi },
  { slug: 'valutazione', title: 'Creare una valutazione', icon: ClipboardList, content: valutazione },
  { slug: 'risultati', title: 'Capire i risultati', icon: Gauge, content: risultati },
  { slug: 'validazione', title: 'Validazione umana', icon: Eye, content: validazione },
  { slug: 'registro', title: 'Il Registro e i suoi stati', icon: ShieldCheck, content: registro },
  { slug: 'istituto', title: 'Gestire l\'istituto', icon: Building2, content: istituto },
  { slug: 'ruoli', title: 'Ruoli e permessi', icon: Users, content: ruoli },
  { slug: 'faq', title: 'FAQ e glossario', icon: HelpCircle, content: faq },
] as const

export default function Guida() {
  const { slug } = useParams()
  const navigate = useNavigate()

  const index = Math.max(0, CHAPTERS.findIndex((c) => c.slug === (slug ?? 'introduzione')))
  const chapter = CHAPTERS[index]
  const prev = index > 0 ? CHAPTERS[index - 1] : null
  const next = index < CHAPTERS.length - 1 ? CHAPTERS[index + 1] : null

  useEffect(() => {
    document.querySelector('main')?.scrollTo({ top: 0 })
  }, [slug])

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex gap-8">
        {/* Chapter sidebar */}
        <aside className="hidden md:block w-60 flex-shrink-0">
          <div className="sticky top-6">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
              Guida di ValutazIA
            </p>
            <nav className="space-y-0.5">
              {CHAPTERS.map((c, i) => (
                <Link
                  key={c.slug}
                  to={`/guida/${c.slug}`}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
                    i === index
                      ? 'bg-mim-lightblue text-mim-blue font-semibold dark:bg-primary/20 dark:text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <c.icon className="w-4 h-4 flex-shrink-0" />
                  {c.title}
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="flex-1 min-w-0 pb-12">
          {/* Mobile chapter picker */}
          <select
            className="md:hidden w-full mb-4 px-3 py-2 rounded-lg border border-border bg-card text-sm"
            value={chapter.slug}
            onChange={(e) => navigate(`/guida/${e.target.value}`)}
          >
            {CHAPTERS.map((c) => (
              <option key={c.slug} value={c.slug}>{c.title}</option>
            ))}
          </select>

          <div className="docs-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => {
                  if (href?.startsWith('/')) {
                    return <Link to={href}>{children}</Link>
                  }
                  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                },
                img: ({ src, alt }) => (
                  <img
                    src={src}
                    alt={alt ?? ''}
                    loading="lazy"
                    className="rounded-xl border border-border shadow-sm my-6 w-full"
                  />
                ),
              }}
            >
              {chapter.content}
            </ReactMarkdown>
          </div>

          {/* Prev / Next */}
          <div className="flex items-stretch gap-3 mt-10 pt-6 border-t border-border">
            {prev ? (
              <Link
                to={`/guida/${prev.slug}`}
                className="flex-1 flex items-center gap-2 p-3 rounded-xl border border-border hover:border-mim-blue/40 hover:bg-muted/50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="min-w-0">
                  <span className="block text-xs text-muted-foreground">Precedente</span>
                  <span className="block text-sm font-medium truncate">{prev.title}</span>
                </span>
              </Link>
            ) : <div className="flex-1" />}
            {next ? (
              <Link
                to={`/guida/${next.slug}`}
                className="flex-1 flex items-center justify-end gap-2 p-3 rounded-xl border border-border hover:border-mim-blue/40 hover:bg-muted/50 transition-colors text-right"
              >
                <span className="min-w-0">
                  <span className="block text-xs text-muted-foreground">Successivo</span>
                  <span className="block text-sm font-medium truncate">{next.title}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </Link>
            ) : <div className="flex-1" />}
          </div>
        </article>
      </div>
    </div>
  )
}
